import { useState, useEffect, useRef, startTransition } from 'react';
import { Modal, formatDate, Combobox, InputTextCustom, LoginSettings } from 'medere-ui-framework';
import { Button } from 'primereact/button';
import { Icon } from '@iconify/react';
import RecipeHistoryView from './RecipeHistory/RecipeHistoryView';
import NewRecipeView from './NewRecipe/NewRecipeView';
import PatientAgenda from './PatientAgenda/PatientAgenda';
import ProfileData from '../Profile/ProfileData';
import CollapsibleCard from '../../components/CollapsibleCard';
import './RecipeView.scss'
import { defaultProfessional, Medicine, PatientRecordDTO, Professional, RequestedPrescriptions, StudyOrderDTO, UserAllowedToPrescript } from '../../interface/interface';
import { ERecipeViews, ESteps, ERecipeHistoryTab, EProfileDataViews } from './RecipeEnums';
import { getPatientData, getProfessionalData, isProfessionalUser, loadRequestedPrescriptions, loadRpDetailsForRequestedPrescription, loadAdministrativeEnabledForPrescription, loadProfessionalsPermissionsForRpGivenToUser, loadWebAppointmentsUrl, rejectPrescriptionRequest, copyDigitalRpById, loadRequestedPrescriptionsForAdministrative, loadPatientByDocAndDocType } from '../../services/digitalRPservice';
import MyQRCode from './PatientAgenda/RegisterAsNewPatientQRCode';
import { addLineBreaksAndStippling } from './Components/GeneralRecipeViewCard';
import { Toast, ToastMessage } from 'primereact/toast';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { useNavigate, useSearchParams } from 'react-router';
import { useRecipeProfessionalData } from '../../context/RecipeProfessionalDataContext';
import { EStudyOrderSteps, EStudyOrderViews } from '../StudyOrder/StudyOrderEnums';
import type { StudyOrderFlowParams } from '../StudyOrder/StudyOrderFlowParams';
import NewStudyOrderView from '../StudyOrder/NewStudyOrder/NewStudyOrderView';
import StudyOrderHistoryView from '../StudyOrder/StudyOrderHistoryView';
import { countStudyOrdersByProfessional, getRecentStudyOrdersByProfessional } from '../../services/StudyOrderController';

const dedupeOrdersByStudyOrder = (rows: StudyOrderDTO[]): StudyOrderDTO[] => {
    const unique = new Map<string, StudyOrderDTO>();
    rows.forEach((row) => {
        const key = String(row?.studyorder ?? '');
        if (!key || unique.has(key)) return;
        unique.set(key, row);
    });
    return Array.from(unique.values());
};

export default function RecipeView() {

    const { patientDocument, patientDocumentType } = useSelector((state: RootState) => state.validationView);


    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const patiententity: number = Number(searchParams.get('medereentityID'))
    const defaultActiveView: number = Number(searchParams.get('activeViewID'))
    const digitalRpId: number = Number(searchParams.get('digitalRpId'))
    const toast = useRef(null)

    const { secretary, setSecretary, activeView, setActiveView, subActiveView, setSubActiveView, prevActiveView, setPrevActiveView, prevSubActiveView, setPrevSubActiveView, setErrorProfileDataFields, errorProfileDataFields } = useRecipeProfessionalData();

    const [selectedPatient, setSelectedPatient] = useState<PatientRecordDTO | null>(null);
    //Paciente que manda el sistema del cliente al entrar por la integración (/login?data=). Se guarda aparte de selectedPatient porque los flujos lo limpian al volver o al emitir, y este tiene que seguir disponible para la próxima receta u orden
    const [integrationPatient, setIntegrationPatient] = useState<PatientRecordDTO | null>(null);
    const [studyOrderInitialPatient, setStudyOrderInitialPatient] = useState<PatientRecordDTO | null>(null);
    const [medications, setMedications] = useState<Medicine[]| null>([]);
    const [prolongedTreatment, setProlongedTreatment] = useState(false);
    const [hidePatientData, setHidePatientData] = useState(false);
    const [goToMedicationStep, setGoToMedicationStep] = useState(false)
    const [recipeDate, setRecipeDate] = useState<Date>(); //Se pone acá y no en NewRecipeView, ya que si editas al paciente se va a la agenda, y al volver al flujo perdería el valor de la fecha. Poniéndolo aca al useState hace que se mantenga el valor al contener tanto el flujo como la agenda

    const [professionals, setProfessionals] = useState<Professional[]>([])
    const [isProfessional, setIsProfessional] = useState<boolean|null>()
    const [allProfileDataFilled, setAllProfileDataFilled] = useState(false);
    const [professional, setProfessional] = useState<Professional>(defaultProfessional);
    const [manuallySelected, setManuallySelected] = useState(false); //se usa si es secretaria, si no se eligió manualmente del combo, se evita filtrar recetas pendientes por profesional y se cambia de profesional dinamicamente segun la receta abierta.
    const [openCard, setOpenCard] = useState<number | null>(null); //se usa si es secretaria, cierra todas las pendientes menos la actual. Al tener a la vista todas las recetas pendientes de todos los profesionales que la asignaron como su secretaria, se evitan conflictos con cerrar todas las pendientes menos la recien abierta.

    const [discardRecipeModal, setDiscardRecipeModal] = useState(false);
    const [indexToRemove, setIndexToRemove] = useState<number>();
    const [cancellationReason, setCancellationReason] = useState<string>("");
    const [errorCancellationReason, setErrorCancellationReason] = useState(false);
    const [requestPrescription, setRequestPrescription] = useState<number | null>(null);

    const [paddingTop, setPaddingTop] = useState('55%');

    const [isMobile, setisMobile] = useState(window.innerWidth < 700)
    const maxWidth = '312px'

    const [openQRWindow, setOpenQRWindow] = useState<boolean>(false)
    const [webAppointmentsUrl, setWebAppointmentsUrl] = useState<string>('')

    const [pendingRecipes, setPendingRecipes] = useState<RequestedPrescriptions[]|null|undefined>([]);
    const [previousStudyOrders, setPreviousStudyOrders] = useState<StudyOrderDTO[]>([]);
    const [previousStudyOrdersCount, setPreviousStudyOrdersCount] = useState<number>(0);
    const [loadingPreviousStudyOrders, setLoadingPreviousStudyOrders] = useState(false);

    const showMessage = (msg: any, ref: React.RefObject<Toast>) => {
        ref.current?.show({ summary: msg, life: 3000 });
    };

    const [digitalRp] = useState<number>(digitalRpId);

    const [refreshPending, setRefreshPending] = useState(false)
    const [disableRefreshPendingRecipes, setDisableFilter] = useState(false) //desahibilita el reload de las recetas pendientes para cuando sos secretaria y manipulas las recetas pendientes sin seleccionar del combo de profesionales primero

    const [loadingRole, setLoadingRole] = useState(true);

    const [loadingPrescriptions, setLoadingPrescriptions] = useState(true);

    const [monthDurationPrescription, setMonthDurationPrescription] = useState({ months: 1, description: '1 mes' })
    const [selectedPreviousStudyOrder, setSelectedPreviousStudyOrder] = useState<StudyOrderDTO | null>(null);

        const openStudyOrderFlow = (
        returnView?: number | null,
        flowParams?: StudyOrderFlowParams,
        initialPatient: PatientRecordDTO | null = null
    ) => {
        const qs = new URLSearchParams(searchParams);
        const paramMap: Array<[string, string | number | undefined]> = [
            ['medereentityID', flowParams?.patientId],
            ['healthPatientServiceId', flowParams?.healthPatientServiceId],
            ['professionalId', flowParams?.professionalId],
            ['studyType', flowParams?.studyType],
            ['mode', flowParams?.mode],
            ['copyFromHpsId', flowParams?.copyFromHpsId],
        ];

        paramMap.forEach(([key, value]) => {
            if (value === undefined || value === null || value === '') {
                qs.delete(key);
                return;
            }
            qs.set(key, String(value));
        });

        startTransition(() => {
            navigate({ search: qs.toString() ? `?${qs.toString()}` : '' }, { replace: true });
            setStudyOrderInitialPatient(initialPatient);
            setPrevActiveView(returnView ?? null);
            setPrevSubActiveView(null);
            setSubActiveView(EStudyOrderSteps.PATIENTSTEP);
            setActiveView(EStudyOrderViews.NEWSTUDYORDER);
        });
    };

    const handleNewStudyOrder = () => {
        openStudyOrderFlow(null, undefined, integrationPatient);
    };

    useEffect(() => {
        setActiveView(defaultActiveView ? defaultActiveView : 0);
        setSubActiveView(defaultActiveView === ERecipeViews.RECIPEHISTORY? ERecipeHistoryTab.FINISHED:0);
    }, [])

    const loadRequestedRecipes = async () => {
        setLoadingPrescriptions(true);
        const requestedRecipes: RequestedPrescriptions[] | null = await loadRequestedPrescriptions(professional?.professionaid, '')
            .then((res) => res)
            .catch((error) => {
                console.error('Error: ', error);
                return [];
            });
        setPendingRecipes(requestedRecipes);
        setLoadingPrescriptions(false);
    };


    const updateSecretaryInfo = async () => {
        const secre:UserAllowedToPrescript = await loadAdministrativeEnabledForPrescription()
                                        .then((res) => {
                                            return res
                                        })
                                        .catch((error) => {
                                            console.error('Error: ', error); 
                                            return null;
                                        });
        setSecretary(secre);
    };

    const updateProfessionalInfo = async () => {
        const professional:Professional = await getProfessionalData()
                                        .then((res) => {
                                            return res
                                        })
                                        .catch((error) => {
                                            console.error('Error: ', error); 
                                            return defaultProfessional;
                                        });
        setProfessional(professional);
        setProfessional(professional); //lo hago otra vez para que siempre se setee el profesional, si no lo hago a veces el profesional no se carga y se tiene que salir y volver a entrar
    };

    const loadRequestedRecipesForAdministrative = async () => {
        setLoadingPrescriptions(true);
        const requestedRecipes: RequestedPrescriptions[] | null = await loadRequestedPrescriptionsForAdministrative()
            .then((res) => res)
            .catch((error) => {
                console.error('Error: ', error);
                return [];
            });
        setPendingRecipes(requestedRecipes);
        setLoadingPrescriptions(false);
    };

    useEffect(() => {
        if (digitalRp > 0)
            copyDigitalRpById(digitalRp)
            .then((res) => {
                setMedications(res.medications)
                setProlongedTreatment(res.prolongedtreatment? res.prolongedtreatment : false)
                setHidePatientData(res.hidepatientdata? res.hidepatientdata : false)

                setPrevSubActiveView(ERecipeHistoryTab.FINISHED)
                setPrevActiveView(activeView)

                setSubActiveView(ESteps.MEDICATIONSTEP)
                setActiveView(ERecipeViews.NEWRECIPE)
            })
    }, [digitalRp]);

    useEffect(() => {
        isProfessionalUser().then((res) => {
            setIsProfessional(res)
        }).finally(() => {
            setLoadingRole(false);
        }).catch((error) => {
            console.error(error);
        });
        updateSecretaryInfo() //para precargar, porque en caso de serlo no llega a cargarse si se carga al fijarse el tipo de usuario
        updateProfessionalInfo()
        
        const handleResize = () => {
            setisMobile(window.innerWidth < 700);
        };
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        const computePaddingTop = () => { //paddingTop para las modales
            const minWidth = 430;
            const maxWidth = 1024;
            const minPadding = 20;
            const maxPadding = 50;
            const windowWidth = window.innerWidth;

            if (windowWidth <= minWidth) {
                setPaddingTop(`${maxPadding}%`);
            } else if (windowWidth >= maxWidth) {
                setPaddingTop(`${minPadding}%`);
            } else {
                const padding = maxPadding - ((windowWidth - minWidth) / (maxWidth - minWidth)) * (maxPadding - minPadding);
                setPaddingTop(`${padding}%`);
            }
        };

        computePaddingTop();
        window.addEventListener('resize', computePaddingTop);

        return () => {
            window.removeEventListener('resize', computePaddingTop);
        };
    }, []);

    useEffect(() => {
        const loadProfessionalsOfSecretary = async () => {
            const profList:Professional[] = await loadProfessionalsPermissionsForRpGivenToUser({filterValue:'', offSet: 0, pageSize: 0})
                                            .then((res) => {
                                                return res
                                            })
                                            .catch((error) => {
                                                console.error('Error: ', error); 
                                                return null;
                                            });
            setProfessionals(profList);
        };

        if (secretary) {
            loadProfessionalsOfSecretary();
        }
    }, [secretary])

    useEffect(() => {
        if (!disableRefreshPendingRecipes && professional.professionaid > 0) {
            loadRequestedRecipes();
        }else { //entra si es secretaria
            loadRequestedRecipesForAdministrative();
        }
    }, [professional]);

    useEffect(() => {
        const loadPreviousStudyOrders = async () => {
            if (!professional?.professionaid || professional.professionaid <= 0) {
                setPreviousStudyOrders([]);
                setPreviousStudyOrdersCount(0);
                return;
            }
            setLoadingPreviousStudyOrders(true);
            const [orders, total] = await Promise.all([
                getRecentStudyOrdersByProfessional(professional.professionaid)
                    .then((res) => res ?? [])
                    .catch((error) => {
                        console.error('Error: ', error);
                        return [];
                    }),
                countStudyOrdersByProfessional(professional.professionaid)
                    .then((res) => res ?? 0)
                    .catch((error) => {
                        console.error('Error: ', error);
                        return 0;
                    }),
            ]);
            const dedupedOrders = dedupeOrdersByStudyOrder(orders);
            setPreviousStudyOrders(dedupedOrders);
            setPreviousStudyOrdersCount(total);
            setLoadingPreviousStudyOrders(false);
        };
        loadPreviousStudyOrders();
    }, [professional?.professionaid]);

    useEffect(() => {
        const loadProfessionalsOfSecretary = async () => {
            const urlWebappointments:string = await loadWebAppointmentsUrl();
            setWebAppointmentsUrl(urlWebappointments)
        };

        loadProfessionalsOfSecretary();
    }, [openQRWindow])

    const loadPatientInformation = async (patientid:number) => {
        const patient:PatientRecordDTO | null = await getPatientData(patientid)
                                        .then((res) => {
                                            return res
                                        })
                                        .catch((error) => {
                                            console.error('Error: ', error); 
                                            return null;
                                        });
        setSelectedPatient(patient);
    };

    const loadPatientInformationByDocument = async (document: string, documentType) => {
        const patient:PatientRecordDTO | null = await loadPatientByDocAndDocType(document, documentType)
                                        .then((res) => {
                                            return res
                                        })
                                        .catch((error) => {
                                            console.error('Error: ', error);
                                            return null;
                                        });
        setIntegrationPatient(patient); //no va a selectedPatient: se entra al dashboard y el paciente se usa recién al elegir Nueva receta o Nueva orden
    };

    useEffect(() => {
        if (loadingRole) return;

        if (goToMedicationStep) {
            setGoToMedicationStep(false)
            setPrevSubActiveView(0)
            setPrevActiveView(0)
    
            setSubActiveView(ESteps.MEDICATIONSTEP)
            setActiveView(ERecipeViews.NEWRECIPE)
        }
        else if (isProfessional && activeView === 0 && subActiveView === 0 && selectedPatient!== null && selectedPatient?.medereentity !== null && selectedPatient?.medereentity !== undefined){ //si es secretaria, no va a medicamentos, va al dashboard
            handleGoToMedicationStepWithPatient()
        }
    }, [selectedPatient, loadingRole])
    
    useEffect(() => {
        if (!loadingRole && patiententity > 0) loadPatientInformation(patiententity);
    }, [patiententity, loadingRole]);

    useEffect(() => {
        if (patientDocument && patientDocumentType)
            loadPatientInformationByDocument(patientDocument, patientDocumentType);
    }, [patientDocument, patientDocumentType]);

    useEffect(() => {
        if (selectedPatient && integrationPatient && selectedPatient.medereentity === integrationPatient.medereentity)
            setIntegrationPatient(selectedPatient);
    }, [selectedPatient]);

    useEffect(() => {
        if (!discardRecipeModal) {
            setCancellationReason('')
            setErrorCancellationReason(false)
        }
    }, [discardRecipeModal])

    useEffect(() => {
        if (refreshPending) {
            if (!disableRefreshPendingRecipes && professional.professionaid > 0)
                loadRequestedRecipes();
            setRefreshPending(false)
        }
    }, [refreshPending]);

    useEffect(() => {
        setAllProfileDataFilled(areAllFieldsFilled());
    }, [professional]);

    useEffect(() => {
        if (cancellationReason && cancellationReason.trim() != "")
            setErrorCancellationReason(false)
    }, [cancellationReason])

    useEffect(() => {
        if (!isProfessional && patiententity && pendingRecipes && !disableRefreshPendingRecipes && professional.professionaid > 0) { //si se eligió un profesional como secretaria, se va a MedicationStep (de ser profesional, se va antes)
            handleGoToMedicationStepWithPatient()
        }
    }, [pendingRecipes])

    const areAllFieldsFilled = () => {
        const {
            firstname,
            lastname,
            document,
            workenrollmenttype,
            workenrollment,
            specialitiesList,
        } = professional;
        
        return( 
            Boolean(
                firstname !== '' && firstname &&
                lastname !== '' && lastname &&
                document !== '' && document.length > 7 && document &&
                workenrollmenttype !== '' && workenrollmenttype &&
                workenrollment !== '' && workenrollment &&
                specialitiesList.length > 0
            )    
        );
    };

    function handleCancellationReasonChange(event: any) {
        setCancellationReason(event.target.value);
    }

    const handleOpenDiscardRecipeModal = (index:number) => {
        setIndexToRemove(index);
        setDiscardRecipeModal(true);
    };

    const handleRemoveRecipe = () => {
        if (!cancellationReason || cancellationReason.trim() == '') {
            setErrorCancellationReason(true)
            return
        }

        const pendingRecipeToRemove:RequestedPrescriptions|undefined = pendingRecipes.filter((_, i) => i === indexToRemove)[0];
        rejectPrescriptionRequest({rejectiondescription:cancellationReason, digitalrp: pendingRecipeToRemove.prescriptionrequest})

        const updatedPendingRecipes:RequestedPrescriptions[]|undefined = pendingRecipes.filter((_, i) => i !== indexToRemove); //notifica al paciente que se descartó su receta
        setPendingRecipes(updatedPendingRecipes);
        setDiscardRecipeModal(false);
    };

    const handleGoToMedicationStep = (recipe:RequestedPrescriptions) => {

        loadPatientInformation(recipe.patientid);

        const loadMedicineInformation = async () => {
            const medicationList:Medicine[]|null = await loadRpDetailsForRequestedPrescription(recipe.prescriptionrequest)
                                                    .then((res) => {
                                                        return res
                                                    })
                                                    .catch((error) => {
                                                        console.error('Error: ', error); 
                                                        return [];
                                                    });
            setMedications(medicationList);
        };

        loadMedicineInformation()

        setRequestPrescription(recipe.prescriptionrequest)
        
        setGoToMedicationStep(true)
    };

    const handleGoToMedicationStepWithPatient = () => {
        setPrevSubActiveView(null)
        setPrevActiveView(null)
        setSubActiveView(ESteps.MEDICATIONSTEP)
        setActiveView(ERecipeViews.NEWRECIPE)
    }

    const handleNewRecipe = () => {
        if (integrationPatient)
            setSelectedPatient(integrationPatient)
        setPrevSubActiveView(null)
        setPrevActiveView(null)
        setSubActiveView(ESteps.PATIENTSTEP)
        setActiveView(ERecipeViews.NEWRECIPE)
    }

    const handleSeeAllPendingRequests = () => {
        setSubActiveView(ERecipeHistoryTab.PENDING)
        setActiveView(ERecipeViews.RECIPEHISTORY)
    }

    const handleSeeHistoryRecipe = () => {
        setSubActiveView(ERecipeHistoryTab.FINISHED)
        setActiveView(ERecipeViews.RECIPEHISTORY)
    }
    const handleSeeStudyOrderHistory = (event?: React.MouseEvent<HTMLElement>) => {
        event?.preventDefault();
        if (!professional?.professionaid || professional.professionaid <= 0) return;
        startTransition(() => {
            setPrevActiveView(null);
            setPrevSubActiveView(null);
            setSubActiveView(0);
            setActiveView(EStudyOrderViews.STUDYORDERHISTORY);
        });
    }

    const handleExitStudyOrderFlow = () => {
        const qs = new URLSearchParams(searchParams);
        qs.delete('mode');
        qs.delete('copyFromHpsId');
        const shouldReturnToHistory = prevActiveView === EStudyOrderViews.STUDYORDERHISTORY;
        navigate({ search: qs.toString() ? `?${qs.toString()}` : '' }, { replace: true });
        setPrevActiveView(null);
        setPrevSubActiveView(null);
        setSubActiveView(0);
        setActiveView(shouldReturnToHistory ? EStudyOrderViews.STUDYORDERHISTORY : 0);
    };

    const handleCopyLink = () => {
        navigator.clipboard.writeText(webAppointmentsUrl).then(() => {
            showMessage('Enlace copiado al portapapeles', toast)
        }).catch(err => {
            console.error('Error al copiar el enlace: ', err);
        });
    };

    const handleProfessionalSelect = (profId:number) => {
        const selectedProfessional = professionals.find(p => p.professionaid === profId);
        if (selectedProfessional) {
            setProfessional(selectedProfessional);
        }
    };
    
    const handleToggleCard = (recipe: RequestedPrescriptions) => {
        if (openCard === recipe.prescriptionrequest) {
            setOpenCard(null);  //cierra la tarjeta si se hace clic de nuevo en la misma
            if (!manuallySelected) {
                setDisableFilter(false);
                setProfessional(defaultProfessional);  //deseleccionar solo si no eligió manualmente al profesional en el combobox
            }
        } else {
            setOpenCard(recipe.prescriptionrequest);  //abre la nueva tarjeta y cierra las demás
            if (!manuallySelected) {
                setDisableFilter(true);
                handleProfessionalSelect(recipe.professionalid);  //selecciona el profesional
            }
        }
    };

    const handleChangeProfesional = (e: Professional) => {
        setManuallySelected(true)
        setDisableFilter(false)
        setProfessional(e)
    }

    const handleEditProfile = () => {
        setErrorProfileDataFields(true)
        setPrevSubActiveView(0)
        setPrevActiveView(0)
        setSubActiveView(EProfileDataViews.PROFILEDATAFORMVIEW)
        setActiveView(ERecipeViews.PROFILEDATA)
    }

    const renderIntegrationPatient = () => {
        if (!integrationPatient) return null;
        const patientDocumentLabel = [patientDocumentType, integrationPatient.document].filter(Boolean).join(' ');
        return (
            <div className="nv-scope recipe-integration-patient">
                <dl className="nv-ro">
                    <dt className="nv-ro-label">Paciente</dt>
                    <dd className="nv-ro-value">
                        {`${integrationPatient.firstname} ${integrationPatient.lastname}`}{patientDocumentLabel ? ` · ${patientDocumentLabel}` : ''}
                    </dd>
                </dl>
            </div>
        );
    };

    const renderRecipeView = () => {
        switch (activeView) {
            case ERecipeViews.RECIPEHISTORY:
                return (
                    <RecipeHistoryView
                        allProfileDataFilled={allProfileDataFilled}

                        activeView={activeView}
                        setActiveView={setActiveView}
                        subActiveView={subActiveView}
                        setSubActiveView={setSubActiveView}

                        prevActiveView={prevActiveView}
                        setPrevActiveView={setPrevActiveView}
                        prevSubActiveView={prevSubActiveView}
                        setPrevSubActiveView={setPrevSubActiveView}

                        medications={medications}
                        setMedications={setMedications}
                        selectedPatient={selectedPatient}
                        setSelectedPatient={setSelectedPatient}
                        setProlongedTreatment={setProlongedTreatment}
                        setHidePatientData={setHidePatientData}
                        
                        professional={professional}
                        pendingRecipes={pendingRecipes}
                        setPendingRecipes={setPendingRecipes}
                        setRequestPrescription={setRequestPrescription}
                        setGoToMedicationStep={setGoToMedicationStep}
                        setProfessional={setProfessional}
                        handleToggleCard={handleToggleCard}
                        openCard={openCard}
                        setOpenCard={setOpenCard}
                        secretary={secretary}
                    />
                );
            case ERecipeViews.NEWRECIPE:
                return (
                    <NewRecipeView
                        activeView={activeView}
                        setActiveView={setActiveView}
                        subActiveView={subActiveView}
                        setSubActiveView={setSubActiveView}

                        prevActiveView={prevActiveView}
                        setPrevActiveView={setPrevActiveView}
                        prevSubActiveView={prevSubActiveView}
                        setPrevSubActiveView={setPrevSubActiveView}
                        
                        medications={medications}
                        setMedications={setMedications}
                        selectedPatient={selectedPatient}
                        setSelectedPatient={setSelectedPatient}
                        recipeDate={recipeDate?? new Date()} setRecipeDate={setRecipeDate}                        prolongedTreatment={prolongedTreatment}
                        setProlongedTreatment={setProlongedTreatment}
                        hidePatientData={hidePatientData}
                        setHidePatientData={setHidePatientData}
                        professional={professional}
                        /* TODO revisar tomy */
                        isProfessional={isProfessional??false}

                        requestPrescription={requestPrescription??0}
                        setRefreshPending={setRefreshPending}

                        monthDurationPrescription={monthDurationPrescription}
                        setMonthDurationPrescription={setMonthDurationPrescription}
                    />
                );
            case ERecipeViews.PATIENTAGENDA:
                return (
                    <PatientAgenda
                        setActiveView={setActiveView}
                        subActiveView={subActiveView}
                        setSubActiveView={setSubActiveView}

                        prevActiveView={prevActiveView}
                        setPrevActiveView={setPrevActiveView}
                        prevSubActiveView={prevSubActiveView}
                        setPrevSubActiveView={setPrevSubActiveView}

                        selectedPatient={selectedPatient}
                        setSelectedPatient={setSelectedPatient}
                        professional={professional}
                    />
                );
            case ERecipeViews.PROFILEDATA:
                return (
                    <ProfileData
                        setActiveView={setActiveView}
                        subActiveView={subActiveView}
                        setSubActiveView={setSubActiveView}

                        prevActiveView={prevActiveView}
                        setPrevActiveView={setPrevActiveView}
                        prevSubActiveView={prevSubActiveView}
                        setPrevSubActiveView={setPrevSubActiveView}

                        medereentity={patiententity}
                        professional={professional}
                        setProfessional={setProfessional}
                        secretary={secretary}
                        setSecretary={setSecretary}
                        errorProfileDataFields={errorProfileDataFields}
                    />
                );
            case EStudyOrderViews.NEWSTUDYORDER:
                return (
                    <NewStudyOrderView
                        professional={professional}
                        initialSelectedPatient={studyOrderInitialPatient ?? undefined}
                        setActiveView={setActiveView}
                        setSubActiveView={setSubActiveView}
                        setPrevActiveView={setPrevActiveView}
                        setPrevSubActiveView={setPrevSubActiveView}

                        onExitFlow={() => {
                            handleExitStudyOrderFlow();
                        }}
                    />
                );
            case EStudyOrderViews.STUDYORDERHISTORY:
                return (
                    <StudyOrderHistoryView
                    professionalId={professional?.professionaid}
                    embedded
                    onOpenOrderFlow={(params) => {
                        openStudyOrderFlow(EStudyOrderViews.STUDYORDERHISTORY, params);
                    }}
                    onBack={() => {
                        setPrevActiveView(null);
                        setPrevSubActiveView(null);
                        setSubActiveView(0);
                        setActiveView(0);
                    }}
                    />
                );
            default:
                return null;
        }
    };

    if (loadingRole) return null;

    if (activeView !== 0)
        return (
            <div className='viewContainer' style={{backgroundColor:'white'}}>
                {renderRecipeView()}
            </div>
        );
    else {
            if (isMobile)
                return (
                    <div className='viewContainer'>
                        {professional?.professionaid > 0 && !allProfileDataFilled && (
                            <div className='warning-not-filled-data'>
                                <Icon icon="mdi:alert" style={{ marginRight: '10px', color:'#FFC107', fontSize:'15px' }} />
                                <p style={{color:'#1E1E1E'}}>Faltan cargar datos del profesional para poder recetar.</p> {secretary ? null : <button type='button' onClick={handleEditProfile} className='link-action-button' style={{ marginLeft:'2px', textDecoration: 'underline', color: '#01717C', fontFamily:'sans-serif', fontStyle:'italic', background:'none', border:'none', padding:0, cursor:'pointer' }}>Editar perfil</button>}
                            </div>
                        )}
                        <div className="recipe-view-container">
                            
                            <div className="header" style={{ marginBottom: '2rem' }}>
                                <div style={{ display: 'flex' }}>
                                    <h1 style={{ fontWeight: 500 }}>Hola,</h1><h1 style={{ fontWeight: 600, marginLeft: '5px' }}>{secretary? (secretary.firstname + " " + secretary.lastname) : (professional?.firstname + " " + professional?.lastname)}</h1>
                                </div>
                                <p style={{ marginTop: '8px', color: 'rgba(24, 39, 58, 0.94)' }}>{secretary? <strong>Secretaría</strong> : professional?.specialitiesList? professional?.specialitiesList.map(speciality => speciality.name).join(', '):''}</p>
                                {secretary? null : <p style={{ fontWeight: 600, color: 'rgba(24, 39, 58, 0.94)' }}>{professional?.workenrollmenttype} {professional?.workenrollment}</p>}
                            </div>

                            {secretary ?
                                <div>
                                    {professionals.length > 0 ?
                                        <div>
                                            <span>Recetar por:</span>
                                            <Combobox
                                                className='border2 mb-4'
                                                getItems = {loadProfessionalsPermissionsForRpGivenToUser}
                                                optionLabel='fulldescription'
                                                placeholder='Profesional'
                                                value={professional}
                                                setValue={(e: Professional) => {
                                                    handleChangeProfesional(e)
                                                }}
                                            /> 
                                        </div> : (
                                            <p className='mb-4' style={{fontWeight: 500}}>No tienes profesionales asignados</p>
                                        )
                                    }
                                </div> : null
                            }

                            {renderIntegrationPatient()}

                            <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '16px', }}>
                                <Button
                                    icon={<Icon icon='vaadin:pills' style={{ fontSize: '34px', display: 'inline', marginRight: '15px', color: 'white' }} />}
                                    iconPos='right'
                                    label={"NUEVA RECETA"}
                                    onClick={handleNewRecipe}
                                    style={{ display: 'block', marginBottom: '4px', paddingTop: '13px', backgroundColor: allProfileDataFilled ? '#3CB8BA' : '#878787', border: 'none', height: '60px', boxShadow: '0 2px 5px rgba(0, 0, 0, 0.2)' }}
                                    disabled={!allProfileDataFilled}
                                />
                                <Button
                                    className="btn-study-order"
                                    icon={<Icon icon='mdi:file-document-outline' style={{ fontSize: '28px', display: 'inline', marginRight: '15px' }} />}
                                    iconPos='right'
                                    label="NUEVA ORDEN"
                                    onClick={handleNewStudyOrder}
                                    style={{
                                        display: 'block',
                                        paddingTop: '2px',
                                        backgroundColor: '#FFFFFF',
                                        color: allProfileDataFilled ? '#3CB8BA' : '#878787',
                                        border: `1px solid ${allProfileDataFilled ? '#3CB8BA' : '#878787'}`,
                                        height: '60px',
                                        boxShadow: '0 2px 5px rgba(0, 0, 0, 0.2)'
                                    }}
                                    disabled={!allProfileDataFilled}
                                />
                            </div>
                            <div style={{display:'flex'}}>
                            <Button
                                className='w-full configuration-button'
                                label="Link para solicitud de recetas"
                                onClick={handleCopyLink}
                                disabled={!allProfileDataFilled}
                            >
                                <i className="pi pi-copy" style={{ color: '#3CB8BA' }}/>
                            </Button>
                            <Button
                                className='configuration-button'
                                style={{marginLeft:'8px', paddingRight:'20px'}}
                                onClick={() => {setOpenQRWindow(true)}}
                                disabled={!allProfileDataFilled}
                            >
                                <i className="pi pi-qrcode" style={{ color: '#3CB8BA' }}/>
                            </Button>
                            </div>
                            <div className="requests-header" style={{ marginTop: '2rem' }}>
                                <h2>Pedidos de recetas</h2>
                            </div>
                            {
                                loadingPrescriptions ? (
                                    <div style={{ marginTop: '1rem', fontStyle: 'italic', color: '#838383' }}>Cargando recetas...</div>
                                ) : (pendingRecipes && pendingRecipes?.length > 0 ? 
                                    <div className="requests-list" style={{ marginTop: '5px' }}>
                                        <div style={{ marginBottom: '1rem', color: '#1E1E1E', display: 'flex', flexDirection: 'row' }}>
                                            Existen <p style={{ fontWeight: 500, marginLeft: '4px' }}>{pendingRecipes.length} recetas pendientes.</p>
                                            <button type='button' onClick={handleSeeAllPendingRequests} className='link-action-button' style={{ marginLeft: '4px', fontFamily: 'sans-serif', fontStyle: 'italic', textDecoration: 'underline', color: '#3CB8BA', background:'none', border:'none', padding:0, cursor:'pointer' }}>Ver todas</button>
                                        </div>
                                        {pendingRecipes.slice(0, 6).map((recipe, index) => (
                                            secretary ? 
                                                <CollapsibleCard
                                                    key={index}
                                                    title={<label style={{ fontWeight: 600, fontSize: '16px', color: 'black' }}>{recipe.patientfullname}</label>}
                                                    subtitle={formatDate(recipe.requesteddate)+" "+recipe.professional}
                                                    style={{ alignSelf: 'start' }}
                                                    //disabled={openCard ? openCard !== recipe.prescriptionrequest : false}
                                                    isOpen={openCard === recipe.prescriptionrequest} //para que se cierren todas menos la que abrí
                                                    onClick={() => {if (secretary) handleToggleCard(recipe)}}
                                                >
                                                    <div>
                                                        {addLineBreaksAndStippling(recipe.medicinesfulldescription, false)}
                                                
                                                        <div style={{ marginTop: '3px', display: 'flex', flexDirection: 'column' }}>
                                                            <Button label={"Recetar"} disabled={!allProfileDataFilled} className="recetar-button" onClick={() => { handleGoToMedicationStep(recipe) }} />
                                                            <Button label={"Descartar"} disabled={!allProfileDataFilled} className="descartar-button" onClick={() => handleOpenDiscardRecipeModal(index)} />
                                                        </div>
                                                    </div>
                                                </CollapsibleCard>
                                            :
                                                <CollapsibleCard
                                                    key={index}
                                                    title={<label style={{ fontWeight: 600, fontSize: '16px', color: 'black' }}>{recipe.patientfullname}</label>}
                                                    subtitle={formatDate(recipe.requesteddate)}
                                                    style={{ alignSelf: 'start' }}
                                                >
                                                    <div>
                                                        {addLineBreaksAndStippling(recipe.medicinesfulldescription, false)}

                                                        <div style={{ marginTop: '3px', display: 'flex', flexDirection: 'column' }}>
                                                            <Button label={"Recetar"} disabled={!allProfileDataFilled} className="recetar-button" onClick={() => { handleGoToMedicationStep(recipe) }} />
                                                            <Button label={"Descartar"} disabled={!allProfileDataFilled} className="descartar-button" onClick={() => handleOpenDiscardRecipeModal(index)} />
                                                        </div>
                                                    </div>
                                                </CollapsibleCard>
                                        ))}
                                    </div>
                                    :
                                    <div style={{ fontFamily:'sans-serif', fontStyle: 'italic', fontSize:'14px', color: '#838383' }}>No hay solicitudes pendientes.<button type='button' onClick={handleSeeHistoryRecipe} className='link-action-button' style={{ marginLeft: '4px', fontFamily: 'sans-serif', fontStyle: 'italic', textDecoration: 'underline', color: '#3CB8BA', background:'none', border:'none', padding:0, cursor:'pointer' }}>Ver realizadas</button></div>
                                )
                            }

                            <div className="requests-header" style={{ marginTop: '2rem' }}>
                                <h2>Ordenes previas</h2>
                            </div>
                            {professional.professionaid <= 0 ? (
                                <div style={{ fontFamily:'sans-serif', fontStyle: 'italic', fontSize:'14px', color: '#838383', marginTop: '5px' }}>
                                    Selecciona un profesional para ver ordenes previas.
                                </div>
                            ) : loadingPreviousStudyOrders ? (
                                <div style={{ fontFamily:'sans-serif', fontStyle: 'italic', fontSize:'14px', color: '#838383', marginTop: '5px' }}>
                                    Cargando ordenes...
                                </div>
                            ) : previousStudyOrders.length === 0 ? (
                                <div style={{ fontFamily:'sans-serif', fontStyle: 'italic', fontSize:'14px', color: '#838383', marginTop: '5px' }}>
                                    No hay ordenes previas.
                                </div>
                            ) : (
                                <div style={{ marginTop: '5px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <div style={{ marginBottom: '1rem', color: '#1E1E1E', display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>
                                        Existen <p style={{ fontWeight: 500, marginLeft: '4px' }}>{previousStudyOrdersCount} ordenes previas.</p>
                                        <button type='button' onClick={handleSeeStudyOrderHistory} className='link-action-button' style={{ marginLeft: '4px', fontFamily: 'sans-serif', fontStyle: 'italic', textDecoration: 'underline', color: '#3CB8BA', background:'none', border:'none', padding:0, cursor:'pointer' }}>Ver historial</button>
                                    </div>
                                    {previousStudyOrders.slice(0, 3).map((order) => (
                                        <button
                                            key={order.studyorder}
                                            type="button"
                                            className="recipe-history-card recipe-history-card-button"
                                            onClick={() => setSelectedPreviousStudyOrder(order)}
                                            style={{ cursor: "pointer", touchAction: "manipulation" }}
                                        >
                                            <div className="recipe-history-card-top">
                                                <div style={{ fontWeight: 600, fontSize: "13px", color: "#1E1E1E" }}>
                                                    {order.patientfullname || "Paciente sin nombre"}
                                                </div>
                                            </div>

                                            <div style={{ fontSize: "12px", color: "#5A6B7B", marginTop: "2px" }}>
                                                Orden #{order.studyorder}
                                            </div>

                                            <div style={{ fontSize: "12px", color: "#3E4D5C", marginTop: "6px", lineHeight: "16px" }}>
                                                {order.icondescription} • {new Date(order.orderdate).toLocaleDateString("es-AR")}
                                            </div>

                                            <div style={{ fontSize: "11px", color: "#6F7E8D", marginTop: "4px", lineHeight: "14px" }}>
                                                {order.presumptivediagnostic ?? `${order.diagnocount ?? 0} estudio(s)`}
                                            </div>
                                            <div className='recipe-history-card-arrow'>
                                                <Icon icon='mdi:chevron-right' />
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}

                            <Modal
                                visible={discardRecipeModal}
                                setVisible={setDiscardRecipeModal}
                                header={<div style={{ display: 'flex', flexDirection: 'column', textAlign: 'center' }}><i className="pi pi-exclamation-circle" style={{ fontSize: '48px', color: '#A53E2B' }}></i>
                                    <label style={{ marginTop: '10px' }}><strong>Descartar receta pendiente</strong></label></div>}
                                headerStyle={{ fontSize: '12px', textAlign: 'center', paddingTop: paddingTop }}
                                closable={false}
                                className='h-full'
                            >
                                <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'center' }}>
                                    <label style={{marginBottom: '1.5rem'}}>Estás por eliminar un pedido pendiente de receta que hizo un paciente. No volverás a tener acceso a ella. El mensaje será visto por el paciente.</label>
                                    
                                    <div style={{ marginBottom: '15px', textAlign: 'left' }}>
                                        <label>Motivo de descarte: </label>
                                        <InputTextCustom 
                                            className={errorCancellationReason ? '' :'border2'}
                                            value={cancellationReason} 
                                            onChange={handleCancellationReasonChange} 
                                            error={errorCancellationReason}
                                            caption ={errorCancellationReason? "Debes detallar el motivo": ""}
                                        />
                                    </div>
                                    <Button
                                        label="Si, descartar"
                                        onClick={handleRemoveRecipe}
                                        style={{ marginBottom: '15px', backgroundColor: '#14C7BC', border: 'none' }}
                                    />
                                    <Button
                                        label="No descartar"
                                        onClick={() => setDiscardRecipeModal(false)}
                                        style={{ backgroundColor: '#FFFFFF', color: '#545454', borderColor:'#3CB8BA' }}
                                    />
                                </div>
                            </Modal>

                            <Modal 
                                visible={openQRWindow} 
                                setVisible={setOpenQRWindow}
                                className='h-full'
                                headerStyle={{ fontSize: '12px', textAlign: 'center'}}
                                header= {
                                    <div className="requests-header" style={{ display:'flex', flexDirection:'column', textAlign: 'center', paddingTop:paddingTop, paddingLeft:'10%' }}>
                                        <h2>Escanee el QR para registrarse como nuevo paciente</h2>
                                    </div>}
                            >
                                <MyQRCode url = {webAppointmentsUrl} />
                            </Modal>
                        </div>

                        <StudyOrderHistoryView
                            professionalId={professional?.professionaid}
                            embedded
                            modalOnly
                            externalOpenOrder={selectedPreviousStudyOrder}
                            onExternalOpenOrderConsumed={() => setSelectedPreviousStudyOrder(null)}
                            onOpenOrderFlow={(params) => {
                                openStudyOrderFlow(null, params);
                            }}
                        />
                        <Toast ref={toast} position='top-center' style={{backgroundColor: 'white'}}/>
                    </div>
                );
            else
                return (
                    <div className='viewContainer'>
                        {professional?.professionaid > 0 && !allProfileDataFilled && (
                            <div className='warning-not-filled-data'>
                                <Icon icon="mdi:alert" style={{ marginRight: '10px', color:'#FFC107', fontSize:'15px' }} />
                                <p style={{color:'#1E1E1E'}}>Faltan cargar datos del profesional para poder recetar.</p> {/*secretary ? null : */<button type='button' className='labelEdit link-action-button' onClick={handleEditProfile} style={{ marginLeft:'2px', textDecoration: 'underline', color: '#01717C', fontFamily:'sans-serif', fontStyle:'italic', background:'none', border:'none', padding:0, cursor:'pointer' }}>Editar perfil</button>}
                            </div>
                        )}
                        <div className="recipe-view-container">
                            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                                <div className="header" style={{ marginBottom: '2rem' }}>
                                    <div style={{ display: 'flex' }}>
                                        <h1 style={{ fontWeight: 500 }}>Hola,</h1><h1 style={{ fontWeight: 600, marginLeft: '5px' }}>{secretary? (secretary.firstname + " " + secretary.lastname) : (professional?.firstname + " " + professional?.lastname)}</h1>
                                    </div>
                                    <p style={{ marginTop: '8px', color: 'rgba(24, 39, 58, 0.94)' }}>{secretary? <strong>Secretaría</strong> : professional?.specialitiesList? professional?.specialitiesList.map(speciality => speciality.name).join(', '):''}</p>
                                    {secretary? null : <p style={{ fontWeight: 600, color: 'rgba(24, 39, 58, 0.94)' }}>{professional?.workenrollmenttype} {professional?.workenrollment}</p>}
                                </div>
                                <div style={{ display: 'flex', height: 'fit-content' }}>
                                    <Button
                                        className='w-full configuration-button'
                                        label="Link para solicitud de recetas"
                                        onClick={handleCopyLink}
                                        disabled={!allProfileDataFilled}
                                    >
                                        <i className="pi pi-copy" style={{ color: '#3CB8BA' }}/>
                                    </Button>
                                    <Button
                                        className='configuration-button'
                                        style={{marginLeft:'8px', paddingRight:'20px'}}
                                        onClick={() => {setOpenQRWindow(true)}}
                                        disabled={!allProfileDataFilled}
                                    >
                                        <i className="pi pi-qrcode" style={{ color: '#3CB8BA' }}/>
                                    </Button>
                                </div>
                            </div> 

                            {secretary ?
                                <div>
                                    {professionals?.length > 0 ?
                                        <div style={{ maxWidth: maxWidth }}>
                                            <span>Recetar por:</span>
                                            <Combobox
                                                className='border2 mb-4'
                                                getItems = {loadProfessionalsPermissionsForRpGivenToUser}
                                                optionLabel='fulldescription'
                                                placeholder='Profesional'
                                                value={professional}
                                                setValue={(e: Professional) => {
                                                    handleChangeProfesional(e)
                                                }}
                                            /> 
                                        </div> : (
                                            <p className='mb-4' style={{fontWeight: 500}}>No tienes profesionales asignados</p>
                                        )
                                    }
                                </div> : null
                            }

                            {renderIntegrationPatient()}

                            <div style={{ display: 'flex', flexDirection: 'row', gap: '16px' }}>
                                <Button
                                    className='btnRpContinue'
                                    icon={<Icon icon='vaadin:pills' style={{ fontSize: '34px', position: 'absolute', left: '20%', color: 'white' }} />}
                                    iconPos='right'
                                    label={"NUEVA RECETA"}
                                    onClick={handleNewRecipe}
                                    style={{ paddingLeft: '50px', marginBottom: '23px', backgroundColor: allProfileDataFilled ? '#3CB8BA' : '#878787', border: 'none', height: '60px', boxShadow: '0 2px 5px rgba(0, 0, 0, 0.2)', maxWidth: maxWidth, flex: 1}}
                                    disabled={!allProfileDataFilled}
                                />
                                <Button
                                    className='btn-study-order'
                                    icon={<Icon icon='mdi:file-document-outline' style={{ fontSize: '28px', position: 'absolute', left: '20%' }} />}
                                    iconPos='right'
                                    label="NUEVA ORDEN"
                                    onClick={handleNewStudyOrder}
                                    style={{paddingLeft: '50px',marginBottom: '23px',backgroundColor: '#FFFFFF',color: allProfileDataFilled ? '#3CB8BA' : '#878787',border: `1px solid ${allProfileDataFilled ? '#3CB8BA' : '#878787'}`,height: '60px',boxShadow: '0 2px 5px rgba(0, 0, 0, 0.2)',maxWidth: maxWidth,flex: 1
                                    }}
                                    disabled={!allProfileDataFilled}
                                />    
                            </div>

                            <div className='recipe-dashboard-grid'>
                                <div className='recipe-dashboard-left'>
                                    <div className="requests-header recipe-section-header">
                                        <h2>Pedidos de recetas</h2>
                                    </div>

                                    {(pendingRecipes && pendingRecipes?.length > 0 ?
                                        <div className="requests-list" style={{ marginTop: '5px' }}>
                                        <div className='recipe-section-subtitle'>
                                            Existen <p style={{ fontWeight: 500, marginLeft: '4px' }}>{pendingRecipes.length} recetas pendientes.</p>
                                            <button type='button' className='linkRp link-action-button' onClick={handleSeeAllPendingRequests} style={{ marginLeft: '4px', fontFamily: 'sans-serif', fontStyle: 'italic', textDecoration: 'underline', color: '#3CB8BA', background:'none', border:'none', padding:0, cursor:'pointer' }}>Ver todas</button>
                                        </div>
                                        <div className='recipe-pending-grid'>
                                            {pendingRecipes.slice(0, 6).map((recipe, index) => (
                                                secretary ?
                                                    <CollapsibleCard
                                                        key={index}
                                                        title={<label style={{ fontWeight: 600, fontSize: '16px', color: 'black' }}>{recipe.patientfullname}</label>}
                                                        subtitle={formatDate(recipe.requesteddate)+" "+recipe.professional}
                                                        style={{ alignSelf: 'start' }}
                                                        //disabled={openCard ? openCard !== recipe.prescriptionrequest : false}
                                                        isOpen={openCard === recipe.prescriptionrequest} //para que se cierren todas menos la que abrí
                                                        onClick={() => {handleToggleCard(recipe)}}
                                                    >
                                                        <div>
                                                            {addLineBreaksAndStippling(recipe.medicinesfulldescription, false)}

                                                            <div style={{ marginTop: '3px', display: 'flex', flexDirection: 'column' }}>
                                                                <Button label={"Recetar"} disabled={!allProfileDataFilled} className="recetar-button" onClick={() => { handleGoToMedicationStep(recipe) }} />
                                                                <Button label={"Descartar"} disabled={!allProfileDataFilled} className="descartar-button" onClick={() => handleOpenDiscardRecipeModal(index)} />
                                                            </div>
                                                        </div>
                                                    </CollapsibleCard>
                                                :
                                                    <CollapsibleCard
                                                        key={index}
                                                        title={<label style={{ fontWeight: 600, fontSize: '16px', color: 'black' }}>{recipe.patientfullname}</label>}
                                                        subtitle={formatDate(recipe.requesteddate)}
                                                        style={{ alignSelf: 'start' }}
                                                    >
                                                        <div>
                                                            {addLineBreaksAndStippling(recipe.medicinesfulldescription, false)}

                                                            <div style={{ marginTop: '3px', display: 'flex', flexDirection: 'column' }}>
                                                                <Button label={"Recetar"} disabled={!allProfileDataFilled} className="recetar-button" onClick={() => { handleGoToMedicationStep(recipe) }} />
                                                                <Button label={"Descartar"} disabled={!allProfileDataFilled} className="descartar-button" onClick={() => handleOpenDiscardRecipeModal(index)} />
                                                            </div>
                                                        </div>
                                                    </CollapsibleCard>
                                            ))}
                                        </div>
                                    </div>
                                    :
                                    <div style={{ fontFamily:'sans-serif', fontStyle: 'italic', fontSize:'14px', color: '#838383' }}>No hay solicitudes pendientes.<button type='button' className='linkRp link-action-button' onClick={handleSeeHistoryRecipe} style={{ marginLeft: '4px', fontFamily: 'sans-serif', fontStyle: 'italic', textDecoration: 'underline', color: '#3CB8BA', background:'none', border:'none', padding:0, cursor:'pointer' }}>Ver realizadas</button></div>
                                    )}
                                </div>

                                <div className='recipe-dashboard-right'>
                                    <div className="requests-header recipe-section-header">
                                        <h2>Ordenes previas</h2>
                                    </div>
                                    {professional.professionaid <= 0 ? (
                                        <div style={{ fontFamily:'sans-serif', fontStyle: 'italic', fontSize:'14px', color: '#838383', marginTop: '5px' }}>
                                            Selecciona un profesional para ver ordenes previas.
                                        </div>
                                    ) : loadingPreviousStudyOrders ? (
                                        <div style={{ fontFamily:'sans-serif', fontStyle: 'italic', fontSize:'14px', color: '#838383', marginTop: '5px' }}>
                                            Cargando ordenes...
                                        </div>
                                    ) : previousStudyOrders.length === 0 ? (
                                        <div style={{ fontFamily:'sans-serif', fontStyle: 'italic', fontSize:'14px', color: '#838383', marginTop: '5px' }}>
                                            No hay ordenes previas.
                                        </div>
                                    ) : (
                                        <div style={{ marginTop: '5px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                            <div className='recipe-section-subtitle' style={{ marginBottom: '4px' }}>
                                                Existen <p style={{ fontWeight: 500, marginLeft: '4px' }}>{previousStudyOrdersCount} ordenes previas.</p>
                                                <button type='button' className='linkRp link-action-button' onClick={handleSeeStudyOrderHistory} style={{ marginLeft: '4px', fontFamily: 'sans-serif', fontStyle: 'italic', textDecoration: 'underline', color: '#3CB8BA', background:'none', border:'none', padding:0, cursor:'pointer' }}>Ver historial</button>
                                            </div>
                                        {previousStudyOrders.slice(0, 3).map((order) => (
                                            <button
                                                key={order.studyorder}
                                                type="button"
                                                className="recipe-history-card recipe-history-card-button"
                                                onClick={() => setSelectedPreviousStudyOrder(order)}
                                                style={{ cursor: "pointer", touchAction: "manipulation" }}
                                            >
                                                <div className="recipe-history-card-top">
                                                <div style={{ fontWeight: 600, fontSize: "13px", color: "#1E1E1E" }}>
                                                    {order.patientfullname || "Paciente sin nombre"}
                                                </div>
                                                </div>

                                                <div style={{ fontSize: "12px", color: "#5A6B7B", marginTop: "2px" }}>
                                                Orden #{order.studyorder}
                                                </div>

                                                <div style={{ fontSize: "12px", color: "#3E4D5C", marginTop: "6px", lineHeight: "16px" }}>
                                                {order.icondescription} • {new Date(order.orderdate).toLocaleDateString("es-AR")}
                                                </div>

                                                <div style={{ fontSize: "11px", color: "#6F7E8D", marginTop: "4px", lineHeight: "14px" }}>
                                                {order.presumptivediagnostic ?? `${order.diagnocount ?? 0} estudio(s)`}
                                                </div>
                                                <div className='recipe-history-card-arrow'>
                                                    <Icon icon='mdi:chevron-right' />
                                                </div>
                                            </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <Modal
                                visible={discardRecipeModal}
                                setVisible={setDiscardRecipeModal}
                                header={<div style={{ display: 'flex', flexDirection: 'column', textAlign: 'center' }}><i className="pi pi-exclamation-circle" style={{ fontSize: '48px', color: '#A53E2B' }}></i>
                                    <label style={{ marginTop: '10px' }}><strong>Descartar receta pendiente</strong></label></div>}
                                headerStyle={{ fontSize: '12px', textAlign: 'center' }}
                                closable={false}
                                width='w-medium'
                            >
                                <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'center' }}>
                                    <label style={{marginBottom: '1.5rem', textAlign: 'left'}}>Estás por eliminar un pedido pendiente de receta que hizo un paciente. No volverás a tener acceso a ella. El mensaje será visto por el paciente.</label>
                                    
                                    <div style={{ marginBottom: '15px', textAlign: 'left' }}>
                                        <label>Motivo de descarte: </label>
                                        <InputTextCustom 
                                            className={errorCancellationReason ? '' :'border2'}
                                            value={cancellationReason} 
                                            onChange={handleCancellationReasonChange} 
                                            error={errorCancellationReason}
                                            caption ={errorCancellationReason? "Debes detallar el motivo": ""}
                                        />
                                    </div>
                                    <Button
                                        label="Si, descartar"
                                        onClick={handleRemoveRecipe}
                                        style={{ marginBottom: '15px', backgroundColor: '#14C7BC', border: 'none' }}
                                    />
                                    <Button
                                        label="No descartar"
                                        onClick={() => setDiscardRecipeModal(false)}
                                        style={{ backgroundColor: '#FFFFFF', color: '#545454', borderColor:'#3CB8BA' }}
                                    />
                                </div>
                            </Modal>
                            <Modal 
                                visible={openQRWindow} 
                                setVisible={setOpenQRWindow}
                                width='w-medium'
                                header= {
                                    <div className="requests-header">
                                        <h2>Escanee el QR para registrarse como nuevo paciente</h2>
                                    </div>}
                            >
                                <MyQRCode url = {webAppointmentsUrl} />
                            </Modal>
                        </div>

                        <StudyOrderHistoryView
                            professionalId={professional?.professionaid}
                            embedded
                            modalOnly
                            externalOpenOrder={selectedPreviousStudyOrder}
                            onExternalOpenOrderConsumed={() => setSelectedPreviousStudyOrder(null)}
                            onOpenOrderFlow={(params) => {
                                openStudyOrderFlow(null, params);
                            }}
                        />
                        <Toast ref={toast} position='top-right' style={{backgroundColor: 'white'}}/>
                    </div>
                );
    }
}
